import { useEffect, useState, useRef } from "react";
import ArrowLeft from '../../assets/svg/arrow-left.svg';
import ArrowRight from '../../assets/svg/arrow-right.svg';

interface CarouselProps {
    images: string[];
    autoForward?: boolean;
    interval?: number;
    parentClassName?: string;
    imageClassName?: string;
    loadingClassName?: string;
}

function Carousel({
    images,
    autoForward = false,
    interval = 3000,
    parentClassName,
    imageClassName,
    loadingClassName
}: CarouselProps) {
    const [activeIndex, setActiveIndex] = useState(0);
    const [cachedImages, setCachedImages] = useState<Map<string, string>>(new Map());
    const [zoom, setZoom] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const dragStart = useRef<{ x: number; y: number } | null>(null);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    // Preload images
    useEffect(() => {
        const cache = new Map<string, string>();
        images.forEach((url) => {
            const img = new Image();
            img.src = url;
            img.onload = () => {
                cache.set(url, url);
                setCachedImages(new Map(cache));
            };
        });
    }, [images]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "ArrowRight") handleNext();
            else if (event.key === "ArrowLeft") handlePrevious();
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [activeIndex]); // eslint-disable-line react-hooks/exhaustive-deps 

    // Auto-forward
    useEffect(() => {
        if (!autoForward) return;
        timerRef.current = setInterval(() => {
            setActiveIndex((prev) => (prev + 1) % images.length);
        }, interval);
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [autoForward, interval, images.length]);

    const pauseAutoForward = () => {
        if (timerRef.current) clearInterval(timerRef.current);
    };

    const resumeAutoForward = () => {
        if (autoForward) {
            timerRef.current = setInterval(() => {
                setActiveIndex((prev) => (prev + 1) % images.length);
            }, interval);
        }
    };

    // Navigation
    const handlePrevious = () => {
        setZoom(1);
        setPosition({ x: 0, y: 0 });
        setActiveIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    };

    const handleNext = () => {
        setZoom(1);
        setPosition({ x: 0, y: 0 });
        setActiveIndex((prev) => (prev + 1) % images.length);
    };

    const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.2, 3));
    
    const handleZoomOut = () => {
        setZoom((prev) => {
            const newZoom = Math.max(prev - 0.2, 1);
            // Reset position if zoom is 1
            if (newZoom === 1) {
                setPosition({ x: 0, y: 0 });
            }
            return newZoom;
        });
    };

    // const resetZoom = () => {
    //     setZoom(1);
    //     setPosition({ x: 0, y: 0 });
    // };

    const handleWheelZoom = (e: React.WheelEvent) => {
        e.preventDefault();
        setZoom((prevZoom) => {
            let newZoom = prevZoom - e.deltaY * 0.001;
            newZoom = Math.min(Math.max(newZoom, 1), 3);
            if (newZoom === 1) {
                setPosition({ x: 0, y: 0 });
            }
            return newZoom;
        });
    };

    // Drag to pan
    const handleMouseDown = (e: React.MouseEvent) => {
        if (zoom <= 1) return;
        setIsDragging(true);
        dragStart.current = { x: e.clientX - position.x, y: e.clientY - position.y };
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isDragging || zoom <= 1) return;
        const newX = e.clientX - (dragStart.current?.x ?? 0);
        const newY = e.clientY - (dragStart.current?.y ?? 0);
        setPosition({ x: newX, y: newY });
    };

    const handleMouseUp = () => setIsDragging(false);
    const handleMouseLeave = () => setIsDragging(false);

    return (
        <div className="flex flex-col items-center relative">
            {/* Zoom Controls */}
            <div className="absolute top-[35px] -mt-[55px] -right-1 flex gap-2 z-10 grid">
                <button
                    onClick={handleZoomOut}
                    className="bg-white shadow-md px-2 rounded-full hover:bg-gray-100 transition"
                    title="Zoom Out"
                >
                    <span className="text-xl font-bold">−</span>
                </button>
                <button
                    onClick={handleZoomIn}
                    className="bg-white shadow-md px-2 rounded-full hover:bg-gray-100 transition"
                    title="Zoom In"
                >
                    <span className="text-xl font-bold">+</span>
                </button>
            </div>
            {/* Main Image */}
            <div
                className={`w-full relative max-w-[450px] ${parentClassName}`}
                onMouseEnter={pauseAutoForward}
                onMouseLeave={resumeAutoForward}
            >
                {cachedImages.has(images[activeIndex]) ? (
                    <div
                        className="overflow-hidden rounded-lg w-full h-80 flex items-center justify-center bg-white"
                        onWheel={handleWheelZoom}
                    >
                        <img
                            src={cachedImages.get(images[activeIndex])}
                            alt={`${activeIndex + 1}`}
                            className={`transition-transform duration-100 ease-in-out object-contain h-[100%] ${imageClassName}`}
                            style={{
                                transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
                                cursor: zoom > 1 ? (isDragging ? "grabbing" : "grab") : "default"
                            }}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseLeave}
                            draggable={false}
                        />
                    </div>
                ) : (
                    <div className={`w-full h-64 flex items-center justify-center bg-gray-200 rounded-lg ${loadingClassName}`}>
                        Loading...
                    </div>
                )}
            </div>

            {/* Arrows */}
            {images.length > 1 && (
                <>
                    <button
                        onClick={handlePrevious}
                        className="absolute left-2 top-[35%] bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 focus:outline-none"
                    >
                        <img src={ArrowLeft} alt='' />
                    </button>
                    <button
                        onClick={handleNext}
                        className="absolute right-2 top-[35%] bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 focus:outline-none"
                    >
                        <img src={ArrowRight} alt='' />
                    </button>
                </>
            )}

            {/* Thumbnails */}
            <div className="flex gap-1 mt-4 overflow-x-auto">
                {images.map((image, index) => (
                    <button
                        key={index}
                        onClick={() => {
                            setZoom(1);
                            setPosition({ x: 0, y: 0 });
                            setActiveIndex(index);
                        }}
                        className="rounded-md hover:opacity-75"
                    >
                        {cachedImages.has(image) ? (
                            <img
                                src={cachedImages.get(image)}
                                alt={`Thumbnail ${index + 1}`}
                                className={`${index === activeIndex ? `w-12 h-12 border-2 border-primary` : `w-10 h-10`} object-cover rounded-lg`}
                            />
                        ) : (
                            <div className="w-10 h-10 bg-gray-200 rounded-lg" />
                        )}
                    </button>
                ))}
            </div>
        </div>
    );
}

export default Carousel;